import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import StyledTableHead from 'Components/StyledTableHead'
import {
  fetchSingleTicket,
  type TicketShape
} from 'Services/SalesManagement/Features/AffiliateProduct/affiliateProductSlice'
import { TransferCounts } from 'Services/SalesManagement/Features/AffiliateProduct/Components/TransferCounts'
import { TransferHistory } from 'Services/SalesManagement/Features/AffiliateProduct/Components/TransferHistory'
import useTypedSelector, { useAppDispatch } from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { t } from 'ttag'

export default function TicketDetailPage() {
  const title = t`Transfer management`
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/transfer-management`
  const previousLocation = usePreviousLocation(baseUrl)
  const dispatch = useAppDispatch()
  const instanceIdParam = parseInt(useParams<{ id: string }>().id as string, 10)

  const [ticketDetails, setTicketDetails] = useState<TicketShape | null>(null)

  const { singleTicketLoading } = useTypedSelector(state => state.affiliateProduct)

  useEffect(() => {
    dispatch(
      fetchSingleTicket({
        id: instanceIdParam
      })
    )
      .then(unwrapResult)
      .then(res => {
        setTicketDetails(res)
      })
  }, [dispatch, instanceIdParam])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: title,
      link: baseUrl
    },
    {
      title: ticketDetails?.external_id ?? ''
    }
  ]

  const isLoading = singleTicketLoading == 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <BackButton label={t`Back`} link={previousLocation} />
        <Box>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table aria-label={t`Ticket details table`}>
              <StyledTableHead>
                <TableRow>
                  <TableCell colSpan={2}>{t`Ticket details`}</TableCell>
                </TableRow>
              </StyledTableHead>
              {isLoading ? (
                <LinearProgress />
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell>{t`External ID`}</TableCell>
                    <TableCell>{showValueOrHyphen(ticketDetails?.external_id ?? null)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t`Product name`}</TableCell>
                    <TableCell>{ticketDetails?.product_name ?? ''}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t`Ticket ID`}</TableCell>
                    <TableCell>{ticketDetails?.ticket_id ?? ''}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t`Payment method`}</TableCell>
                    <TableCell>
                      {showValueOrHyphen(ticketDetails?.payment_method ?? null)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t`External creation date`}</TableCell>
                    <TableCell>
                      {datetimeFormatter(ticketDetails?.external_creation_date ?? null)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t`Payment status`}</TableCell>
                    <TableCell>{ticketDetails?.payment_status ?? ''}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{'Waltti ID'}</TableCell>
                    <TableCell>{showValueOrHyphen(ticketDetails?.waltti_id ?? null)}</TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Box>
        <Box>
          <TransferCounts ticketId={instanceIdParam} />
        </Box>
        <Box>
          <TransferHistory ticketId={instanceIdParam} />
        </Box>
      </Box>
    </Box>
  )
}
